import usePasswordCriteria from '@liveauctioneers/caterwaul-components/lib/hooks/usePasswordCriteria';
import PasswordCriteriaList from '@liveauctioneers/caterwaul-components/lib/PasswordCriteriaList/PasswordCriteriaList';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Warning } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import * as React from 'react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import PageTitle from '@/components/PageTitle/PageTitle';
import useIsServerSideRendered from '@/hooks/useIsServerSideRendered';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getHasLoggedIn } from '@/redux/modules/account/login/login.selectors';
import { openForgotPasswordModal, openLoginModal } from '@/redux/modules/modal';
import { getUiPasswordReset, submitPasswordReset } from '@/redux/modules/passwordReset';
import { getHomeUrl } from '@/utils/urls';

export type CreateNewPasswordFormProps = {
    bidderId: number;
    hash: string;
};

type PasswordResetFormData = {
    confirmPassword: string;
    newPassword: string;
};

/**
 * A form for changing the user's current password
 */
const CreateNewPasswordForm = ({ bidderId, hash }: CreateNewPasswordFormProps) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const {
        formState: { errors, isSubmitted },
        handleSubmit,
        register,
        watch,
    } = useForm<PasswordResetFormData>({
        mode: 'onChange',
    });

    const newPassword = watch('newPassword', '');
    const confirmPassword = watch('confirmPassword', '');

    const { passwordValid } = usePasswordCriteria(newPassword);
    const passwordResetUIState = useAppSelector(getUiPasswordReset);

    const hasLoggedIn = useAppSelector(getHasLoggedIn);

    const isSSR = useIsServerSideRendered();

    useEffect(() => {
        if (hasLoggedIn) {
            navigate(getHomeUrl());
        }
    }, [hasLoggedIn, navigate]);

    const onSubmitPasswordChange = handleSubmit((data) => {
        if (passwordValid && data.newPassword === data.confirmPassword) {
            dispatch(submitPasswordReset({ bidderId, hash, newPassword: data.newPassword }));
        }
    });

    const errorMessage =
        passwordResetUIState.errorMessage === 'Incorrect reset code.' ? (
            <FormattedMessage id="passwordReset.errorIncorrectCode" />
        ) : passwordResetUIState.errorMessage === 'Reset code has expired.' ? (
            <FormattedMessage id="passwordReset.errorResetCodeExpired" />
        ) : (
            <FormattedMessage id="passwordReset.error" />
        );

    if (passwordResetUIState.success) {
        return (
            <main className="mx-auto flex h-full w-[438px] flex-col justify-center py-[20px] pb-[60px] tabletMax:w-[calc(100vw-40px)]">
                <PageTitle
                    className="w-full text-center capitalize"
                    title={<FormattedMessage id="passwordReset.success.title" />}
                />
                <div className="flex flex-col items-center text-center">
                    <BodyPrimary className="mb-lg tabletMax:mb-md">
                        <FormattedMessage id="passwordReset.success.description" />
                    </BodyPrimary>
                    <Button
                        className="mt-[36px] w-full desktop:w-[214px]"
                        onClick={() => {
                            dispatch(openLoginModal());
                        }}
                    >
                        <FormattedMessage id="passwordReset.success.logIn" />
                    </Button>
                </div>
            </main>
        );
    }

    return (
        <form
            className="mx-auto flex h-full w-[347px] flex-col justify-center py-[20px] pb-[60px]"
            onSubmit={onSubmitPasswordChange}
        >
            <PageTitle
                className="w-full text-center capitalize"
                title={<FormattedMessage id="passwordReset.title" />}
            />
            {isSSR ? (
                <div className="flex h-[250px] items-center justify-center">
                    <Loading size={32} />
                </div>
            ) : (
                <div className="mt-sm flex flex-col gap-sm">
                    <TextInput
                        {...register('newPassword', {
                            required: true,
                        })}
                        aria-label="new-password"
                        className="mb-xs"
                        data-testid="resetPasswordNewPassword"
                        id="new-password"
                        labelText={formatMessage({
                            id: 'passwords.changePassword.newPassword',
                        })}
                        type="password"
                    />
                    <TextInput
                        {...register('confirmPassword', {
                            required: true,
                            validate: (value) =>
                                value === newPassword || formatMessage({ id: 'passwords.errors.passwordsDoNotMatch' }),
                        })}
                        aria-label="confirm-password"
                        data-testid="resetPasswordConfirmPassword"
                        errorText={isSubmitted ? errors.confirmPassword?.message : undefined}
                        hasError={isSubmitted && Boolean(errors.confirmPassword)}
                        id="confirm-password"
                        labelText={formatMessage({
                            id: 'passwords.changePassword.confirmNewPassword',
                        })}
                        type="password"
                    />
                    <PasswordCriteriaList
                        className="pl-0"
                        password={newPassword}
                        showErrorFeedback={isSubmitted && !passwordValid}
                    />
                </div>
            )}
            <Button
                data-testid="resetPasswordSetPassword"
                disabled={passwordResetUIState.submitted || !passwordValid || !confirmPassword}
                loading={passwordResetUIState.submitted}
                onClick={onSubmitPasswordChange}
                type="submit"
            >
                <FormattedMessage id="passwords.changePassword.setPassword" />
            </Button>

            {passwordResetUIState.error && (
                <div className="mt-sm flex items-center gap-xs">
                    <Warning
                        color="critical"
                        style="fill"
                    />
                    <div className="inline gap-xs">
                        <BodyPrimary color="critical">{errorMessage}</BodyPrimary>
                        <InlineButton
                            className="ml-2xs"
                            onClick={() => dispatch(openForgotPasswordModal())}
                            size="md"
                        >
                            {formatMessage({ id: 'passwordReset.errorTryAgain' })}
                        </InlineButton>
                    </div>
                </div>
            )}
        </form>
    );
};

export default CreateNewPasswordForm;
